import React, { type ReactNode, type CSSProperties } from 'react';
import { RECORD_ICONS } from 'javascripts/record_icons';
import Color from 'color';
import {
  Competitor,
  DiscoveryInterview,
  DiscoveryStudy,
  Epic,
  Feature,
  Goal,
  Idea,
  Initiative,
  Page,
  Persona,
  Project,
  Release,
  ReleasePhase,
  Requirement,
  Responsibility,
  Task,
  TaskUser,
  User,
  KeyResult,
  Poll,
  IdeaTheme,
  Iteration,
} from 'components/framework/models';
import type Query from 'components/framework/Query';
import classNames from 'classnames';
import pluralize from 'javascripts/util/pluralize';
import { dateFormattedWithUserLocale } from 'javascripts/util/dateFns';
import styles from '../../../Reference.module.css';
import { mixColorForTheme } from 'javascripts/theme';
import DatePill from 'components/common/Attributes/DatePill';
import { type ReferenceAhaObject } from '@aha-app/aha-editor-v2';

export const RECORD_CLASSES: Record<string, ReferenceRecordType> = {
  Competitor,
  Epic,
  Feature,
  Goal,
  Idea,
  Initiative,
  Iteration,
  Page,
  Persona,
  Release,
  ReleasePhase,
  Requirement,
  Task,
  Poll,
  IdeaTheme,
  KeyResult,
  // Include other names
  StrategicImperative: Goal,
  'Ideas::Idea': Idea,
  'Ideas::IdeaTheme': IdeaTheme,
  'Discovery::Study': DiscoveryStudy,
  'Discovery::Interview': DiscoveryInterview,
  'Polls::Poll': Poll,
};

export type ReferenceRecord =
  | Competitor
  | DiscoveryInterview
  | DiscoveryStudy
  | Epic
  | Feature
  | Goal
  | Idea
  | Initiative
  | Iteration
  | Page
  | Persona
  | Release
  | ReleasePhase
  | Requirement
  | Task
  | KeyResult
  | Poll
  | IdeaTheme;

export type ReferenceRecordType =
  | typeof Competitor
  | typeof DiscoveryInterview
  | typeof DiscoveryStudy
  | typeof Epic
  | typeof Feature
  | typeof Goal
  | typeof Idea
  | typeof Initiative
  | typeof Iteration
  | typeof Page
  | typeof Persona
  | typeof Release
  | typeof ReleasePhase
  | typeof Requirement
  | typeof Task
  | typeof KeyResult
  | typeof Poll
  | typeof IdeaTheme;

export type RecordType = keyof typeof RECORD_CLASSES;

export type Field = {
  key: string;
  title: string;
  // If separator is false, it will never have a separator placed around it. If `true`, it might have a separator if
  // it's next to another field that is not `false`.
  separator?: boolean;
  available: (
    modelClass: ReferenceRecordType,
    itemClass: ReferenceAhaObject['class']
  ) => boolean;
  scope: (query: Query<any, any>) => Query<any, any>;
  render: (
    record: ReferenceRecord,
    itemClass: ReferenceAhaObject['class']
  ) => React.ReactNode;
};

const fields: Map<string, Field> = new Map();

function defineField(key: string, fieldDefinition: Omit<Field, 'key'>) {
  const field = { key, ...fieldDefinition };

  fields.set(key, field);
}

defineField('icon', {
  title: 'Icon',
  separator: false,
  available(modelClass, itemClass) {
    return itemClass in RECORD_ICONS;
  },
  scope(query) {
    if (query.model.hasField('color')) {
      return query.select('color');
    }

    return query;
  },
  render(record, itemClass) {
    const style: CSSProperties = { display: 'inline-block' };
    if ('colorObject' in record && record.colorObject instanceof Color) {
      style.color = record.colorObject.hex();
    }

    let iconClass = RECORD_ICONS[itemClass];
    if (itemClass === 'Task') {
      iconClass = 'fa-solid fa-circle-check';
    }

    return (
      <i
        className={classNames(iconClass, styles.icon, 'aha-reference-icon')}
        style={style}
      />
    );
  },
});

defineField('ref', {
  title: 'Reference #',
  available(modelClass, itemClass) {
    return modelClass.hasField('referenceNum');
  },
  scope(query) {
    return query.select('referenceNum');
  },
  render(record, itemClass) {
    if ('referenceNum' in record && record.referenceNum) {
      return record.referenceNum;
    }

    // Fallback for reference number from editor data
    if ('ref' in record && record.ref) {
      return record.ref as ReactNode;
    }

    return null;
  },
});

defineField('name', {
  title: 'Name',
  separator: true,
  available(modelClass, itemClass) {
    return modelClass.hasField('name');
  },
  scope(query) {
    return query.select('name');
  },
  render(record, itemClass) {
    if (record.name) {
      if ('completedDate' in record && record.completedDate) {
        return <s>{record.name}</s>;
      } else {
        return record.name;
      }
    }
  },
});

defineField('status', {
  title: 'Status',
  separator: false,
  available(modelClass, itemClass) {
    return modelClass.hasField('workflowStatus');
  },
  scope(query) {
    return query.merge({ workflowStatus: ['name', 'color'] });
  },
  render(record, itemClass) {
    if (
      'workflowStatus' in record &&
      record.workflowStatus?.colorObject instanceof Color
    ) {
      return (
        <aha-pill
          color={mixColorForTheme(record.workflowStatus.color).hex()}
          size='small'
        >
          {record.workflowStatus.name}
        </aha-pill>
      );
    }
  },
});

defineField('assignees', {
  title: 'Assigned to',
  separator: false,
  available(modelClass, itemClass) {
    return modelClass.hasField('taskUsers');
  },
  scope(query) {
    return query.merge({
      taskUsers: TaskUser.select('id', 'status').merge({
        assignee: User.select('id', 'name', 'avatarUrl', 'initials')
          .union(Project.select('id'))
          .union(Responsibility.select('id')),
      }),
    });
  },
  render(record, itemClass) {
    if ('taskUsers' in record && record.taskUsers?.length) {
      return record.taskUsers.map((taskUser, index) => {
        const { assignee: user } = taskUser;
        if (user instanceof Project || user instanceof Responsibility) {
          return null;
        }

        return (
          <aha-avatar
            size={14}
            src={user.avatarUrl}
            name={user.name}
            label={user.initials}
            style={{
              marginLeft: index > 0 ? '-1.4em' : '',
            }}
          />
        );
      });
    }
  },
});

defineField('assignedToUser', {
  title: 'Assigned to',
  separator: false,
  available(modelClass, itemClass) {
    return modelClass.hasField('assignedToUser');
  },
  scope(query) {
    return query.merge({
      assignedToUser: User.select('name', 'avatarUrl', 'initials'),
    });
  },
  render(record, itemClass) {
    if ('assignedToUser' in record && record.assignedToUser instanceof User) {
      return (
        <aha-pill size='small' border-shape='round'>
          <aha-avatar
            size={14}
            src={record.assignedToUser.avatarUrl}
            name={record.assignedToUser.name}
            label={record.assignedToUser.initials}
            style={{
              marginLeft: '-1em',
              marginRight: '0.2em',
            }}
          />
          {record.assignedToUser.name}
        </aha-pill>
      );
    }
  },
});

defineField('release_date', {
  title: 'Release date',
  separator: true,
  available(modelClass, itemClass) {
    return modelClass.hasField('releaseDate');
  },
  scope(query) {
    return query.select('releaseDate');
  },
  render(record, itemClass) {
    if ('releaseDate' in record && record.releaseDate) {
      return dateFormattedWithUserLocale(record.releaseDate);
    }
  },
});

defineField('due_date', {
  title: 'Due date',
  separator: false,
  available(modelClass, itemClass) {
    return (
      modelClass.hasField('dueDate') && modelClass.hasField('recurrenceRule')
    );
  },
  scope(query) {
    return query.select('dueDate', 'recurrenceRule');
  },
  render(record, itemClass) {
    if ('dueDate' in record && 'recurrenceRule' in record && record.dueDate) {
      return (
        <span className='task'>
          <DatePill
            date={record.dueDate}
            recurrence={!!record.recurrenceRule}
            completed={!!record.completedDate}
          />
        </span>
      );
    }
  },
});

defineField('epoch', {
  title: 'Time frame',
  separator: true,
  available(modelClass, itemClass) {
    return modelClass.hasField('epoch');
  },
  scope(query) {
    return query.merge({ epoch: ['name'] });
  },
  render(record) {
    if ('epoch' in record && record.epoch?.name) {
      return record.epoch.name;
    }
  },
});

defineField('votes', {
  title: 'Vote count',
  separator: true,
  available(modelClass, itemClass) {
    return modelClass.hasField('votes');
  },
  scope(query) {
    return query.select('votes');
  },
  render(record) {
    if ('votes' in record && typeof record.votes === 'number') {
      return `${record.votes} ${pluralize(record.votes, 'vote')}`;
    }
  },
});

export function defaultFields(
  itemClass: ReferenceAhaObject['class']
): string[] {
  switch (itemClass) {
    case 'Task':
      return ['name'];
    case 'Persona':
    case 'Competitor':
      return ['icon', 'name'];
    default:
      return ['icon', 'ref'];
  }
}

export function defaultScope(
  modelClass: ReferenceRecordType
): Query<ReferenceRecordType, ReferenceRecord> {
  let scope = modelClass.select('id', 'path');

  switch (modelClass) {
    case Task:
      scope = scope
        .select('completedByMe', 'completedDate', 'type')
        .where({ personal: false });
  }
  return scope;
}

export function fieldsForItem(itemClass: ReferenceAhaObject['class']): Field[] {
  const modelClass = RECORD_CLASSES[itemClass];
  if (!modelClass) {
    return [];
  }

  return Array.from(fields.values()).filter(field =>
    field.available(modelClass, itemClass)
  );
}
